import { Client } from "@/types/page";
import { Price } from "@/types/price";
import { PricingVersion } from "@/types/pricingVersion";
import { PromotionCode } from "@/types/PromotionCode";
import {
    applyDiscountToCents,
    formatMoneyWithCurrency,
} from "@/utils/moneyFormatting";
import pluralize from "@/utils/plural";

type V11PricingCardProps = {
    price: Price;
    pricingVersion: PricingVersion;
    promotionCode: PromotionCode | null;
    client: Client;
    show50MinVariant?: string | null;
};

export function V11PricingCard(props: V11PricingCardProps) {
    const trialBaseAmountForDisplay =
        props.pricingVersion.trialCostAmountForDisplay ??
        (props.price.priceGroup &&
        props.pricingVersion.trialCostAmountPerPriceGroupForDisplay
            ? props.pricingVersion.trialCostAmountPerPriceGroupForDisplay[
                  props.price.priceGroup
              ]
            : 0) ??
        0;
    const baseCents =
        props.pricingVersion.trialCostAmountCents ??
        (props.price.priceGroup &&
        props.pricingVersion.trialCostAmountCentsPerPriceGroup
            ? props.pricingVersion.trialCostAmountCentsPerPriceGroup[
                  props.price.priceGroup
              ]
            : 0) ??
        0;
    const costAfterPromoCents = applyDiscountToCents(
        baseCents,
        props.promotionCode?.coupon,
    );
    const costAfterPromoForDisplay = formatMoneyWithCurrency(
        costAfterPromoCents,
        props.client.currency ?? "usd",
    );

    const includesCopy =
        props.show50MinVariant === "treatment"
            ? "Includes a 50 min video session + messaging"
            : "Includes video session + messaging";

    return (
        <div
            data-testid="pricing-explanation-container"
            className="rounded-lg border border-brand-border p-4 text-brand-black text-md sm:text-lg"
        >
            {props.promotionCode ? (
                <div>
                    <span className="font-bold">
                        Cost today:{" "}
                        <span className="line-through">
                            {trialBaseAmountForDisplay}
                        </span>{" "}
                        {costAfterPromoForDisplay}
                    </span>{" "}
                    for 1 week trial
                </div>
            ) : (
                <div data-testid="pricing-and-interval">
                    <span className="font-bold">
                        Cost today: {trialBaseAmountForDisplay}
                    </span>{" "}
                    for 1 week trial
                </div>
            )}
            <div data-testid="pricing-includes" className="italic">
                {includesCopy}
            </div>
            <div
                data-testid="pricing-after-trial-per-week"
                className="font-bold mt-4"
            >
                After 1 week trial: {props.price.amountPerSessionForDisplay}
                /week
            </div>
            <div data-testid="pricing-billed-amount" className="italic">
                Billed as {props.price.amountForDisplay} every{" "}
                {pluralize(props.price.intervalCount, props.price.interval)}
            </div>
        </div>
    );
}
